import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotificationPopup from '../../utility/NotificationPopup';
import BootstrapDatePicker from '../../utility/BootstrapDatePicker';
import AutocompleteDropdown from '../../utility/AutocompleteDropdown';
import useAutocomplete from '../../hooks/useAutocomplete';
import useApplicationStore from '../../store/applicationStore';
import { CREDIT_API_URL, TRUSTPAISA_URL, CASHE_URL } from '../../Constants';
import '../../assets/css/application.css';


const EligibilityFormSection = () => {
  const navigate = useNavigate();

  // Zustand store
  const { leadData: storeLeadData, setLeadData: setStoreLeadData } = useApplicationStore();

  // Calculate default date (22 years ago) in DD/MM/YYYY format
  const getDefaultDate = () => {
    const today = new Date();
    const defaultDate = new Date(today.getFullYear() - 22, today.getMonth(), today.getDate());
    const day = String(defaultDate.getDate()).padStart(2, '0');
    const month = String(defaultDate.getMonth() + 1).padStart(2, '0');
    const year = defaultDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse DD/MM/YYYY format to Date object for calculations
  const parseDateForCalculation = (dateString) => {
    if (!dateString) return null;

    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }

    // Handle YYYY-MM-DD format (fallback)
    return new Date(dateString);
  };

  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: getDefaultDate(),
    profession: 'salaried',
    companyName: '',
    monthlyIncome: '',
    salaryMode: 'direct-bank',
    pincode: '',
    consent: false
  });

  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState('');
  const [leadData, setLeadData] = useState(null);
  const [loanAmount, setLoanAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success',
    redirectUrl: null,
    showLoader: false,
    lenderName: null,
    openInNewTab: false
  });

  // Pincode autocomplete using reusable hook
  const pincodeAutocomplete = useAutocomplete({
    apiEndpoint: '/pincode/search',
    minChars: 2,
    debounceDelay: 200,
    onSelect: (item) => {
      setFormData(prev => ({
        ...prev,
        pincode: item.pinCode || item
      }));
      if (errors.pincode) {
        setErrors(prev => ({
          ...prev,
          pincode: ''
        }));
      }
      validateField('pincode', item.pinCode || item);
    },
    getValue: (item) => item.pinCode || item,
    getKey: (item, index) => item.pinCode || index
  });

  // Company autocomplete using reusable hook
  const companyAutocomplete = useAutocomplete({
    apiEndpoint: '/company/search',
    minChars: 2,
    debounceDelay: 200,
    onSelect: (item) => {
      setFormData(prev => ({
        ...prev,
        companyName: item.companyName || item
      }));
      if (errors.companyName) {
        setErrors(prev => ({
          ...prev,
          companyName: ''
        }));
      }
      validateField('companyName', item.companyName || item);
    },
    getValue: (item) => item.companyName || item,
    getKey: (item, index) => item.id || item.companyName || index
  });

  // Load lead data from Zustand store on component mount
  useEffect(() => {
    if (storeLeadData) {
      setLeadData(storeLeadData);
    }

    // Also check sessionStorage for loan amount as fallback
    const storedLoanAmount = sessionStorage.getItem('loanAmount');
    if (storedLoanAmount) {
      setLoanAmount(parseInt(storedLoanAmount));
    }
  }, [storeLeadData]);

  // Prefill form data from leadData.employmentDetails or eligibility.inputs
  useEffect(() => {
    if (leadData) {
      const empDetails = leadData.employmentDetails;
      const eligibilityInputs = leadData.eligibility?.inputs;

      // Helper to convert YYYY-MM-DD to DD/MM/YYYY
      const convertDateToDDMMYYYY = (dateString) => {
        if (!dateString) return getDefaultDate();
        if (dateString.includes('-')) {
          const parts = dateString.split('-');
          if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
        }
        return dateString;
      };

      // Helper to map employment type
      const mapEmploymentType = (type) => {
        if (!type) return 'salaried';
        const lowerType = type.toLowerCase();
        if (lowerType.includes('salaried')) return 'salaried';
        if (lowerType.includes('self')) return 'self-employed';
        if (lowerType.includes('business')) return 'business';
        return 'salaried';
      };

      // Helper to map salary received type
      const mapSalaryMode = (type) => {
        if (!type) return 'direct-bank';
        const lowerType = type.toLowerCase();
        if (lowerType.includes('bank') || lowerType.includes('direct')) return 'direct-bank';
        if (lowerType.includes('cheque')) return 'cheque';
        if (lowerType.includes('cash')) return 'cash';
        return 'direct-bank';
      };

      setFormData(prev => ({
        ...prev,
        name: leadData.name || prev.name,
        pincode: empDetails?.pinCode || eligibilityInputs?.pinCode || prev.pincode,
        dateOfBirth: convertDateToDDMMYYYY(empDetails?.dateOfBirth || eligibilityInputs?.dateOfBirth) || prev.dateOfBirth,
        profession: mapEmploymentType(empDetails?.employmentType || eligibilityInputs?.employmentType),
        companyName: empDetails?.companyName || eligibilityInputs?.companyName || prev.companyName,
        monthlyIncome: empDetails?.monthlySalary?.toString() || eligibilityInputs?.monthlySalary?.toString() || prev.monthlyIncome,
        salaryMode: mapSalaryMode(empDetails?.salaryReceivedType || eligibilityInputs?.salaryReceivedType)
      }));
    }
  }, [leadData]);

  // Check if form is valid for enabling/disabling submit button
  const isFormValid = () => {
    // Check if all required fields are filled
    const hasAllFields =
      formData.dateOfBirth &&
      formData.profession &&
      formData.companyName &&
      formData.monthlyIncome &&
      formData.salaryMode &&
      formData.pincode &&
      formData.consent;

    // Check if there are no validation errors
    const hasNoErrors = Object.keys(errors).length === 0;

    // Check if monthly income meets minimum requirement
    const hasValidIncome = formData.monthlyIncome && parseInt(formData.monthlyIncome) >= 10000;

    // Check if age is within valid range (22-55 years)
    let hasValidAge = false;
    if (formData.dateOfBirth) {
      const today = new Date();
      const birthDate = parseDateForCalculation(formData.dateOfBirth);
      if (birthDate && !isNaN(birthDate.getTime())) {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        hasValidAge = age >= 22 && age <= 55;
      }
    }

    return hasAllFields && hasNoErrors && hasValidIncome && hasValidAge;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let processedValue = value;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Real-time validation for each field
    validateField(name, type === 'checkbox' ? checked : processedValue);

    // Check if user selected non-eligible options
    if (name === 'profession' && value !== 'salaried' && value !== 'self-employed') {
      setSelectedProfession(value);
      setShowModal(true);
    }
  };

  // Real-time field validation
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };

    switch (fieldName) {
      case 'dateOfBirth':
        if (!value) {
          newErrors.dateOfBirth = 'Date of Birth is required';
        } else {
          const today = new Date();
          const birthDate = parseDateForCalculation(value);
          if (birthDate && !isNaN(birthDate.getTime())) {
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }

            if (age < 22 || age > 55) {
              newErrors.dateOfBirth = 'Age must be between 22 to 55 years';
            } else {
              delete newErrors.dateOfBirth;
            }
          } else {
            newErrors.dateOfBirth = 'Please enter a valid date';
          }
        }
        break;

      case 'profession':
        if (!value) {
          newErrors.profession = 'Please select your profession';
        } else {
          delete newErrors.profession;
        }
        break;

      case 'companyName':
        if (!value || value.trim().length === 0) {
          newErrors.companyName = 'Company name is required';
        } else if (value.trim().length < 2) {
          newErrors.companyName = 'Company name must be at least 2 characters';
        } else {
          delete newErrors.companyName;
        }
        break;

      case 'monthlyIncome':
        if (!value) {
          newErrors.monthlyIncome = 'Monthly income is required';
        } else if (parseInt(value) < 10000) {
          newErrors.monthlyIncome = 'Monthly income should be greater than or equal to ₹10,000';
        } else {
          delete newErrors.monthlyIncome;
        }
        break;

      case 'salaryMode':
        if (!value) {
          newErrors.salaryMode = 'Please select how you receive your salary';
        } else {
          delete newErrors.salaryMode;
        }
        break;

      case 'pincode':
        if (!value) {
          newErrors.pincode = 'Pincode is required';
        } else if (value.length !== 6) {
          newErrors.pincode = 'Pincode must be 6 digits';
        } else {
          delete newErrors.pincode;
        }
        break;

      case 'consent':
        if (!value) {
          newErrors.consent = 'Please accept the terms and conditions';
        } else {
          delete newErrors.consent;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleUnderstandClick = () => {
    setShowModal(false);
    setSelectedProfession('');
    navigate('/');
  };

  const getModalMessage = () => {
    return `Thank you for your interest! We will not be able to proceed further with your application. Currently we are only offering loans to salaried and self-employed individuals.`;
  };

  const handleCancelClick = () => {
    setShowModal(false);
    setSelectedProfession('');
    // Reset to default values
    setFormData(prev => ({
      ...prev,
      profession: 'salaried'
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Date of Birth validation (22 to 55 years)
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of Birth is required';
    } else {
      const today = new Date();
      const birthDate = parseDateForCalculation(formData.dateOfBirth);
      if (birthDate && !isNaN(birthDate.getTime())) {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        if (age < 22 || age > 55) {
          newErrors.dateOfBirth = 'Age must be between 22 to 55 years';
        }
      } else {
        newErrors.dateOfBirth = 'Please enter a valid date';
      }
    }

    // Profession validation
    if (!formData.profession) {
      newErrors.profession = 'Please select your profession';
    }

    // Company Name validation
    if (!formData.companyName || formData.companyName.trim().length === 0) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    // Monthly Income validation
    if (!formData.monthlyIncome) {
      newErrors.monthlyIncome = 'Monthly income is required';
    } else if (parseInt(formData.monthlyIncome) < 10000) {
      newErrors.monthlyIncome = 'Monthly income should be greater than or equal to ₹10,000';
    }

    // Salary Mode validation
    if (!formData.salaryMode) {
      newErrors.salaryMode = 'Please select how you receive your salary';
    }

    // Pincode validation
    if (!formData.pincode) {
      newErrors.pincode = 'Pincode is required';
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    // Consent validation
    if (!formData.consent) {
      newErrors.consent = 'Please accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      if (!leadData || !leadData.id) {
        console.error('No lead data found');
        return;
      }

      setIsProcessing(true);
      try {
        // Convert DD/MM/YYYY to YYYY-MM-DD for API
        const convertDateForAPI = (dateString) => {
          if (!dateString) return '';
          if (dateString.includes('/')) {
            const parts = dateString.split('/');
            if (parts.length === 3) {
              const day = parts[0];
              const month = parts[1];
              const year = parts[2];
              return `${year}-${month}-${day}`;
            }
          }
          return dateString; // Return as-is if already in correct format
        };

        // Call eligibility check API
        const eligibilityPayload = {
          leadId: leadData.id,
          name: formData.name?.trim() || '',
          dateOfBirth: convertDateForAPI(formData.dateOfBirth), // Convert DD/MM/YYYY to YYYY-MM-DD
          employmentType: formData.profession === 'salaried' ? 'Salaried' : 'Self Employed',
          companyName: formData.companyName.trim(),
          salaryReceivedType: formData.salaryMode === 'direct-bank' ? 'Direct Bank Account' :
            formData.salaryMode === 'cheque' ? 'Cheque' : 'Cash',
          monthlySalary: parseInt(formData.monthlyIncome),
          pincode: formData.pincode
        };

        console.log('Eligibility payload:', eligibilityPayload);

        const response = await axios.post(`${CREDIT_API_URL}/eligibility/check`, eligibilityPayload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        console.log('Eligibility response:', response.data);

        // Update leadData in Zustand store with eligibility information (regardless of eligible status)
        setStoreLeadData({ ...leadData, eligibility: response.data });

        // Get loanAmount from eligibility response inputs, or from state/sessionStorage, or from leadData
        const eligibilityLoanAmount = response.data.inputs?.loanAmount;
        const leadDataLoanAmount = leadData?.loanAmount;
        const finalLoanAmount = eligibilityLoanAmount || leadDataLoanAmount || loanAmount || 0;

        // Map employmentType for lender allocation API
        const mapEmploymentType = (profession) => {
          switch (profession) {
            case 'salaried':
              return 'Salaried';
            case 'business':
              return 'Business';
            case 'self-employed':
              return 'Self Employed';
            case 'other':
              return 'Other';
            default:
              return 'Salaried';
          }
        };

        // Map salaryReceivedType for lender allocation API
        const mapSalaryReceivedType = (salaryMode) => {
          switch (salaryMode) {
            case 'direct-bank':
              return 'Bank';
            case 'cheque':
              return 'Cheque';
            case 'cash':
              return 'Cash';
            default:
              return 'Bank';
          }
        };

        // Call lender allocation API - ALWAYS call regardless of eligibility check result
        const lenderAllocationPayload = {
          leadId: leadData.id, // Add leadId if available for lender assignment tracking
          pinCode: formData.pincode,
          loanAmount: finalLoanAmount,
          loanType: 'Personal Loan',
          employmentType: mapEmploymentType(formData.profession),
          monthlySalary: parseInt(formData.monthlyIncome),
          salaryReceivedType: mapSalaryReceivedType(formData.salaryMode),
          dateOfBirth: convertDateForAPI(formData.dateOfBirth)
        };

        // Debug logging
        console.log('DEBUG - leadData:', leadData);
        console.log('DEBUG - leadStatus:', leadData.leadStatus);
        console.log('DEBUG - lenderId:', leadData.eligibility?.lenderId);

        // If lead was previously rejected, exclude that lender from allocation
        if (leadData.leadStatus === 'Rejected' && leadData.eligibility?.lenderId) {
          lenderAllocationPayload.excludeLenderId = leadData.eligibility.lenderId;
          console.log('DEBUG - excludeLenderId added:', lenderAllocationPayload.excludeLenderId);
        }

        console.log('Lender allocation payload:', lenderAllocationPayload);

        try {
          const lenderAllocationUrl = `${CREDIT_API_URL}/lender/allocate`;

          console.log('Lender allocation API URL:', lenderAllocationUrl);

          const lenderAllocationResponse = await axios.post(lenderAllocationUrl, lenderAllocationPayload, {
            headers: {
              'Content-Type': 'application/json'
            }
          });

          console.log('Lender allocation response:', lenderAllocationResponse.data);

          // Use new API response for redirect - process based on allocation API result
          if (lenderAllocationResponse.data && lenderAllocationResponse.data.eligible) {
            const lenderId = lenderAllocationResponse.data.lenderId;
            const lenderName = lenderAllocationResponse.data.lenderName;
            const lenderLink = lenderAllocationResponse.data.lenderLink;

            console.log('Lender allocation redirect data:', { lenderId, lenderName, lenderLink });

            let notificationData;

            if (lenderId === 'CASHE_001') {
              // Redirect to internal application journey
              notificationData = {
                show: true,
                message: `Great! 🙂 You meet our lender's basic criteria. `,
                type: 'success',
                redirectUrl: '/application/journey',
                showLoader: true,
                lenderName: 'cashe',
                openInNewTab: false
              };
            } else if (lenderLink) {
              // For all other lenders, open in new tab
              notificationData = {
                show: true,
                message: `Great! 🙂 You meet our lender's basic criteria.`,
                type: 'success',
                redirectUrl: lenderLink,
                showLoader: true,
                lenderName: lenderName?.toLowerCase().replace(/\s+/g, ''),
                openInNewTab: true
              };
            } else {
              // Fallback notification
              notificationData = {
                show: true,
                message: `Thank you for your interest! We will not be able to proceed further with your application.`,
                type: 'success',
                redirectUrl: null,
                showLoader: false,
                lenderName: null,
                openInNewTab: false
              };
            }

            console.log('Setting notification data:', notificationData);
            setNotification(notificationData);
          } else {
            // Lender allocation API says not eligible
            setNotification({
              show: true,
              message: `Thank you for your interest! We will not be able to proceed further with your application.`,
              type: 'error',
              redirectUrl: null,
              showLoader: false,
              lenderName: null,
              openInNewTab: false
            });
          }
        } catch (lenderAllocationError) {
          console.error('Lender allocation API error:', lenderAllocationError);
          // Fallback to eligibility response if lender allocation fails
          const lenderId = response.data.lenderId;
          const lenderName = response.data.lenderName;
          const lenderLink = response.data.lenderLink;

          let notificationData;

          if (lenderId === 'CASHE_001') {
            notificationData = {
              show: true,
              message: `Great! 🙂 You meet our lender's basic criteria. `,
              type: 'success',
              redirectUrl: '/application/journey',
              showLoader: true,
              lenderName: 'cashe',
              openInNewTab: false
            };
          } else if (lenderLink) {
            notificationData = {
              show: true,
              message: `Great! 🙂 You meet our lender's basic criteria.`,
              type: 'success',
              redirectUrl: lenderLink,
              showLoader: true,
              lenderName: lenderName?.toLowerCase().replace(/\s+/g, ''),
              openInNewTab: true
            };
          } else {
            notificationData = {
              show: true,
              message: `Thank you for your interest! We will not be able to proceed further with your application.`,
              type: 'error',
              redirectUrl: null,
              showLoader: false,
              lenderName: null,
              openInNewTab: false
            };
          }

          setNotification(notificationData);
        }

        // Clear session storage
        sessionStorage.removeItem('loanAmount');
      } catch (error) {
        console.error('Error checking eligibility:', error);
        setNotification({
          show: true,
          message: 'An error occurred while checking eligibility. Please try again.',
          type: 'error',
          redirectUrl: null,
          showLoader: false,
          lenderName: null
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="application-section eligibility-form-section">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            {!leadData ? (
              <div className="application-container eligibility-form-container">
                <div className="text-center p-4">
                  <h3>No Lead Data Found</h3>
                  <p>Please start your application from the homepage first.</p>
                  <Button
                    onClick={() => navigate('/')}
                    className="emi-homepage-start-application-btn"
                  >
                    Go to Homepage
                  </Button>
                </div>
              </div>
            ) : (
              <div className="application-container eligibility-form-container">
                <Form onSubmit={handleSubmit}>

                  {/* Name */}
                  <Row className="mb-4 align-items-center">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Enter your Name
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <div className="emi-homepage-input-field-container">
                        <div className={`emi-homepage-input-field ${errors.name ? 'emi-homepage-input-field-error' : ''}`}>
                          <input
                            type="text"
                            name="name"
                            value={formData.name || ''}
                            onChange={handleChange}
                            className="emi-homepage-input-field-text"
                            placeholder="Enter your name"
                          />
                        </div>
                      </div>
                      {errors.name && (
                        <div className="emi-homepage-validation-error">{errors.name}</div>
                      )}
                    </Col>
                  </Row>
                  {/* Pincode */}
                  <Row className="mb-4 align-items-center">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Enter your Pin Code
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <div className="emi-homepage-input-field-container" style={{ position: 'relative' }}>
                        <div className={`emi-homepage-input-field ${errors.pincode ? 'emi-homepage-input-field-error' : ''}`}>
                          <input
                            ref={pincodeAutocomplete.inputRef}
                            type="text"
                            name="pincode"
                            value={formData.pincode || ''}
                            onChange={(e) => {
                              const filteredValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                              setFormData(prev => ({
                                ...prev,
                                pincode: filteredValue
                              }));

                              // Clear error when user starts typing
                              if (errors.pincode) {
                                setErrors(prev => ({
                                  ...prev,
                                  pincode: ''
                                }));
                              }

                              // Validate field
                              validateField('pincode', filteredValue);

                              // Trigger debounced search for autocomplete
                              pincodeAutocomplete.debouncedSearch(filteredValue);
                            }}
                            onFocus={() => {
                              if (pincodeAutocomplete.suggestions.length > 0) {
                                pincodeAutocomplete.setShowDropdown(true);
                              }
                            }}
                            inputMode="numeric"
                            maxLength={6}
                            className="emi-homepage-input-field-text"
                            placeholder="Enter your pincode"
                          />
                        </div>

                        {/* Pincode Suggestions Dropdown */}
                        <AutocompleteDropdown
                          suggestions={pincodeAutocomplete.suggestions}
                          showDropdown={pincodeAutocomplete.showDropdown}
                          isLoading={pincodeAutocomplete.isLoading}
                          onSelect={pincodeAutocomplete.handleSelect}
                          getValue={pincodeAutocomplete.getValue}
                          getKey={pincodeAutocomplete.getKey}
                          dropdownRef={pincodeAutocomplete.dropdownRef}
                        />
                      </div>
                      {errors.pincode && (
                        <div className="emi-homepage-validation-error">{errors.pincode}</div>
                      )}
                    </Col>
                  </Row>
                  {/* Date of Birth */}
                  <Row className="mb-4 align-items-center">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Select your Date of Birth
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <BootstrapDatePicker
                        name="dateOfBirth"
                        id="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        placeholder="Select your date of birth"
                        maxDate={new Date().toISOString().split('T')[0]}
                        error={errors.dateOfBirth}
                        className="eligibility-datepicker"
                      />
                    </Col>
                  </Row>

                  {/* Profession */}
                  <Row className="mb-4">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Select Current Employment Type
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <div className="employment-type-grid-container">
                        {/* First row: Salaried and Business */}
                        <div className="employment-type-row">
                          {['salaried', 'business'].map((option) => (
                            <div key={option} className="radio-option employment-type-option">
                              <input
                                type="radio"
                                id={`profession-${option}`}
                                name="profession"
                                value={option}
                                checked={formData.profession === option}
                                onChange={handleChange}
                                className="radio-input"
                              />
                              <label htmlFor={`profession-${option}`} className="radio-label">
                                {option === 'salaried' ? 'Salaried' : 'Business'}
                              </label>
                            </div>
                          ))}
                        </div>
                        {/* Second row: Self Employed and Other */}
                        <div className="employment-type-row">
                          {['self-employed', 'other'].map((option) => (
                            <div key={option} className="radio-option employment-type-option">
                              <input
                                type="radio"
                                id={`profession-${option}`}
                                name="profession"
                                value={option}
                                checked={formData.profession === option}
                                onChange={handleChange}
                                className="radio-input"
                              />
                              <label htmlFor={`profession-${option}`} className="radio-label">
                                {option === 'self-employed' ? 'Self Employed' : 'Other'}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      {errors.profession && (
                        <div className="emi-homepage-validation-error">{errors.profession}</div>
                      )}
                    </Col>
                  </Row>

                  {/* Company Name */}
                  <Row className="mb-4 align-items-center">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Enter your Company Name
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <div className="emi-homepage-input-field-container" style={{ position: 'relative' }}>
                        <div className={`emi-homepage-input-field ${errors.companyName ? 'emi-homepage-input-field-error' : ''}`}>
                          <input
                            ref={companyAutocomplete.inputRef}
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                companyName: value
                              }));

                              // Clear error when user starts typing
                              if (errors.companyName) {
                                setErrors(prev => ({
                                  ...prev,
                                  companyName: ''
                                }));
                              }

                              // Validate field
                              validateField('companyName', value);

                              // Trigger debounced search for autocomplete
                              companyAutocomplete.debouncedSearch(value);
                            }}
                            onFocus={() => {
                              if (companyAutocomplete.suggestions.length > 0) {
                                companyAutocomplete.setShowDropdown(true);
                              }
                            }}
                            className="emi-homepage-input-field-text"
                            placeholder="Enter company name"
                          />
                        </div>

                        {/* Company Suggestions Dropdown */}
                        <AutocompleteDropdown
                          suggestions={companyAutocomplete.suggestions}
                          showDropdown={companyAutocomplete.showDropdown}
                          isLoading={companyAutocomplete.isLoading}
                          onSelect={companyAutocomplete.handleSelect}
                          getValue={companyAutocomplete.getValue}
                          getKey={companyAutocomplete.getKey}
                          dropdownRef={companyAutocomplete.dropdownRef}
                        />
                      </div>
                      {errors.companyName && (
                        <div className="emi-homepage-validation-error">{errors.companyName}</div>
                      )}
                    </Col>
                  </Row>

                  {/* Monthly Income */}
                  <Row className="mb-4 align-items-center">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Enter your Monthly Salary
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <div className="emi-homepage-input-field-container">
                        <div className={`emi-homepage-input-field ${errors.monthlyIncome ? 'emi-homepage-input-field-error' : ''}`}>
                          <span className="emi-homepage-input-prefix">₹</span>
                          <input
                            type="number"
                            name="monthlyIncome"
                            value={formData.monthlyIncome}
                            onChange={handleChange}
                            className="emi-homepage-input-field-text"
                            placeholder="Monthly income"
                            min="10000"
                          />
                        </div>
                      </div>
                      {errors.monthlyIncome && (
                        <div className="emi-homepage-validation-error">{errors.monthlyIncome}</div>
                      )}
                    </Col>
                  </Row>

                  {/* Salary Mode */}
                  <Row className="mb-4">
                    <Col md={5} sm={12} className="mb-3 mb-md-0">
                      <label className="form-label">
                        Select mode of Salary Payment
                      </label>
                    </Col>
                    <Col md={7} sm={12}>
                      <div className="salary-mode-group">
                        {['direct-bank', 'cheque', 'cash'].map((option) => (
                          <div key={option} className="radio-option salary-mode-option">
                            <input
                              type="radio"
                              id={`salaryMode-${option}`}
                              name="salaryMode"
                              value={option}
                              checked={formData.salaryMode === option}
                              onChange={handleChange}
                              className="radio-input"
                            />
                            <label htmlFor={`salaryMode-${option}`} className="radio-label">
                              {option === 'direct-bank' ? 'Direct Bank Account' :
                                option === 'cheque' ? 'Cheque' : 'Cash'}
                            </label>
                          </div>
                        ))}
                      </div>
                      {errors.salaryMode && (
                        <div className="emi-homepage-validation-error">{errors.salaryMode}</div>
                      )}
                    </Col>
                  </Row>

                  {/* T&C Consent - Centered */}
                  <Row className="justify-content-center">
                    <Col md={8} sm={12} >
                      <div className="homepage-consent-section" style={{ alignItems: 'center' }}>
                        <div className={`homepage-consent-content ${errors.consent ? 'homepage-consent-section-error' : ''}`}>
                          <input
                            type="checkbox"
                            name="consent"
                            checked={formData.consent}
                            onChange={handleChange}
                            className="homepage-consent-checkbox"
                          />
                          <span className="homepage-consent-text">
                            I agree to the {' '}<Link to="/privacy-policy" className="privacy-link">Terms & Conditions</Link> and{' '}
                            <Link to="/privacy-policy" className="privacy-link">
                              Privacy Policy
                            </Link>
                          </span>
                        </div>
                        {errors.consent && (
                          <div className="homepage-consent-error">
                            <div className="emi-homepage-validation-error">{errors.consent}</div>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Submit Button - Centered */}
                  <Row className="mb-4 justify-content-center">
                    <Col md={8} sm={12} className="text-center">
                      <Button
                        type="submit"
                        className="emi-homepage-start-application-btn"
                        style={{ minWidth: '200px', margin: '0 auto' }}
                        disabled={isProcessing || !isFormValid()}
                      >
                        {isProcessing ? 'Checking Eligibility...' : 'Check Eligibility'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Eligibility Modal */}
      <Modal show={showModal} onHide={handleCancelClick} centered>
        <Modal.Body className="text-center p-4">
          <div className="warning-icon mb-3">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ffc107' }}></i>
          </div>
          <p className="mb-4" style={{ fontSize: '16px', color: '#333' }}>
            {getModalMessage()}
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              onClick={handleCancelClick}
              style={{ minWidth: '120px' }}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleUnderstandClick}
              style={{ minWidth: '120px' }}
            >
              I Understand
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Notification Popup */}
      <NotificationPopup
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({
          show: false,
          message: '',
          type: 'success',
          redirectUrl: null,
          showLoader: false,
          lenderName: null,
          openInNewTab: false
        })}
        redirectUrl={notification.redirectUrl}
        showLoader={notification.showLoader}
        lenderName={notification.lenderName}
        openInNewTab={notification.openInNewTab}
      />
    </div>
  );
};

export default EligibilityFormSection;
